
import stringUtil from '../../string/stringUtil';
import logger from '../../logger/logger';
import dateUtil from '../../dateUtil/dateUtil';

const domainConstants = require('../../domain/constants');
const cardPaymentManagement = domainConstants.externalEntities.cardPaymentManagement;
export class HandPointRestAPIDriver
{
    constructor(url, apiKey, terminalId) 
    {
        this.url = url;
        this.apiKey = apiKey;
        this.terminalId = terminalId;

        this.handPointDeviceConfiguration = null;
        this.requestIndex = 0;

        this.isConnected = false;
        this.currentTransaction = null;

        this.checkResultTimeInMs = 1000;
        this.timeoutTransactionInMinute = 5;
    }

    getNextRequestReference()
    {
        this.requestIndex = this.requestIndex + 1;
        return `request_ref_${this.requestIndex}`;
    }

    getHttpHeader()
    {
        return ({
            "Content-Type": "application/json",
            "ApiKeyCLoud": this.apiKey,
        });
    }

    async initializeHandPoint()
    {
        var url = `${this.url}/initialize`;
        var request = {};
        request.method = "GET";
        request.headers = this.getHttpHeader();

        var response = await fetch(url, request);

        if (!response.ok)
            throw `Error returned when accessing resource 'initialize' with status code ${response.status} by handpoint endpoint. ${response.statusText}`;

        var rawResponse = await response.json();
        if (Array.isArray(rawResponse))
        {
            if (rawResponse.length <= 0)
                throw `No Device configuration returned by handpoint server`;

            const terminalResponse = rawResponse.find(x => stringUtil.areStringSame(`${x.serial_number}-${x.terminal_type}`, this.terminalId));
            if (terminalResponse == null)
                throw `No Device configuration matching the terminalId returned by handpoint server`;

            this.handPointDeviceConfiguration = terminalResponse;
        }
        else
        {
            this.handPointDeviceConfiguration = rawResponse
        }
    }

    async pingHandPointTerminal()
    {
        var url = `${this.url}/transactions`;
        var request = {};
        request.method = "POST";
        request.headers = this.getHttpHeader();

        var body = {};
        body.operation = "pingDevice";
        body.terminal_type = this.handPointDeviceConfiguration.terminal_type;
        body.serial_number = this.handPointDeviceConfiguration.serial_number;
        body.customerReference = this.getNextRequestReference();

        request.body = JSON.stringify(body);

        var response = await fetch(url, request);
        if (!response.ok)
            throw `Error returned when accessing resource 'transactions(pingDevice)' with status code ${response.status} by handpoint endpoint. ${response.statusText}`;
    }

    async establishConnection()
    {
        if (stringUtil.isStringNullOrEmpty(this.url))
            throw "Failed to find card terminal address";

        if (stringUtil.isStringNullOrEmpty(this.apiKey))
            throw "Failed to find card terminal key";

        await this.initializeHandPoint();
        await this.pingHandPointTerminal();
        this.isConnected = true;
    }

    async connect(onConnect, onConnectionError)
    {
        try
        {
            await this.establishConnection();
            onConnect();
        }
        catch (error)
        {
            logger.warn(`Error occurred in handpoint driver while initializing the connection with handpoint credit card terminal: ${error}`, {}, error, cardPaymentManagement.handPoint);
            onConnectionError(error);
        }
    }

    async chargeAmount(amount, onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline)
    {
        await this.performTransaction("sale", amount, onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline);
    }

    async refundAmount(amount, onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline)
    {
        await this.performTransaction("refund", amount, onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline);
    }

    async performTransaction(operation, amount, onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline)
    {
        try
        {
            if (this.currentTransaction != null)
            {
                throw `There is already an ongoing transaction(${this.currentTransaction.id}) and cannot start a new one.`;
            }

            if (!this.isConnected)
            {
                await this.establishConnection();
            }

            onStatusMessage("Connecting To Device")

            var url = `${this.url}/transactions`;
            var request = {};
            request.method = "POST";
            request.headers = this.getHttpHeader();

            var body = {};
            body.operation = operation;
            body.amount = `${(amount * 100).toFixed(0) * 1}`;
            body.currency = "GBP";
            body.terminal_type = this.handPointDeviceConfiguration.terminal_type;
            body.serial_number = this.handPointDeviceConfiguration.serial_number;
            body.customerReference = this.getNextRequestReference();

            request.body = JSON.stringify(body);


            var response = await fetch(url, request);
            if (!response.ok)
                throw `Error returned when accessing resource 'transactions(${operation})' with status code ${response.status} by handpoint endpoint. ${response.statusText}`;

            var rawResponse = await response.json();
            this.currentTransaction = {};
            this.currentTransaction.startDateTime = dateUtil.getNow();
            this.currentTransaction.id = rawResponse.transactionResultId;
            onStatusMessage("Device Connected, Waiting For Customer");

            this.setTimerForTransactionResult(onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline);
        }
        catch (error)
        {
            logger.warn(`Error occurred in handpoint driver while performing transaction: ${error}`, {}, error, cardPaymentManagement.handPoint);
            onError(error);
        }
    }

    async getTransactionResult(onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline)
    {
        try
        {
            if (this.currentTransaction == null)
                return;

            if (dateUtil.differenceInMinutes(dateUtil.getNow(), this.currentTransaction.startDateTime) >= this.timeoutTransactionInMinute)
            {
                onError("TimeOut");
                this.currentTransaction = null;
            }

            var url = `${this.url}/transaction-result/${this.currentTransaction.id}`;
            var request = {};
            request.method = "GET";
            request.headers = this.getHttpHeader();

            var response = await fetch(url, request);
            if (!response.ok)
                throw `Error returned when accessing resource 'transaction-result' with status code ${response.status} by handpoint endpoint. ${response.statusText}`;

            var rawTextResponse = await response.text();
            if (stringUtil.isStringNullOrEmpty(rawTextResponse))
            {
                this.setTimerForTransactionResult(onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline);
                return;
            }

            var rawJsonResponse = JSON.parse(rawTextResponse);
            if (stringUtil.isStringNullOrEmpty(rawJsonResponse.finStatus))
            {
                this.setTimerForTransactionResult(onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline);
                return;
            }

            if (rawJsonResponse.finStatus == "AUTHORISED")
            {
                var formattedResponse = this.convertCreditCardResponseToStandardResponse(rawJsonResponse);
                onSuccessful(formattedResponse);
                this.currentTransaction = null;
            }
            else
            {
                onDecline();
                this.currentTransaction = null;
            }
        }
        catch (error)
        {
            logger.warn(`Error occurred handpoint driver while retrieving the transaction result: ${error}`, {}, error, cardPaymentManagement.handPoint);
            this.setTimerForTransactionResult(onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline);
            return;
        }
    }

    setTimerForTransactionResult(onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline)
    {
        window.setTimeout(() =>
        {
            this.getTransactionResult(onStatusMessage, onError, onSignatureVerification, onSignatureVerificationTimeout, onSuccessful, onDecline);
        }, this.checkResultTimeInMs);
    }

    convertCreditCardResponseToStandardResponse(clientResponse)
    {
        var dateTime = new Date(clientResponse.efttimestamp);

        clientResponse.authCode = clientResponse.authorisationCode;
        clientResponse.amountTotal = clientResponse.requestedAmount;
        clientResponse.cardSchemeName = clientResponse.cardSchemeName;
        clientResponse.dateOfExpiry = clientResponse.expiryDateMMYY;
        clientResponse.primaryAccountNumber = clientResponse.maskedCardNumber;
        clientResponse.cardholderVerificationMethod = clientResponse.paymentScenario;
        clientResponse.paymentMethod = clientResponse.verificationMethod;
        clientResponse.tpi = clientResponse.tid;
        clientResponse.transactionTime = dateTime.toString();
        clientResponse.transactionDate = dateTime.toString();
        clientResponse.transactionId = clientResponse.transactionID;

        clientResponse.driver = "HandPoint";
        return clientResponse;
    }
}
